import React from 'react'
import { useActions } from 'src/hooks'
import {
  PhoneNumberInput,
  VerifyPhoneNumberFailureFragment,
  VerifyPhoneNumberSuccessFragment,
  VerticalId,
} from 'src/graphql/generated/graphql'
import { PhoneNumberVerificationDialog } from './PhoneNumberVerificationDialog'
import { usePhoneNumberVerification } from 'src/react_query'
import { PhoneNumberVerificationRaw } from './PhoneNumberVerificationRaw'

type PhoneNumberVerificationDialogForAccountProps = {
  sponsorGenealogyIdOrSlug?: string
  isCreateOrLogin: boolean
  shouldNotCreateNewAccount: boolean
  title?: string
  anchorButton?: {
    label: string
    action: () => void
  } | null
  shouldHideCardHeroImg?: boolean
  validateButtonTitle?: string
  dialog?: {
    isOpen: boolean
    onClose: () => void
  }
  onChangeFirstAndLastName?: (firstname?: string, lastname?: string) => void
  onSuccess: (
    success: VerifyPhoneNumberSuccessFragment,
    possibleRedirectUri: string | null,
  ) => void
}

export const PhoneNumberVerificationDialogForAccount = (
  props: PhoneNumberVerificationDialogForAccountProps,
) => {
  const {
    sponsorGenealogyIdOrSlug,
    isCreateOrLogin,
    shouldNotCreateNewAccount,
    title,
    anchorButton,
    shouldHideCardHeroImg,
    validateButtonTitle,
    dialog,
    onChangeFirstAndLastName,
    onSuccess,
  } = props

  const actions = useActions()
  const verifyPhoneNumberMutation = usePhoneNumberVerification()

  const verifyPhoneNumber = async (
    phoneNumber: PhoneNumberInput,
    verificationCode: string,
    hasSmsOptIn?: boolean,
  ): Promise<void | VerifyPhoneNumberFailureFragment> => {
    if (verificationCode.length !== 6) {
      throw new Error('Verification code must 6 digits.')
    }
    try {
      const {
        verifyPhoneNumber: result,
        createUserLoginLink: { link },
      } = await verifyPhoneNumberMutation.mutateAsync({
        phoneNumber,
        verificationCode,
        sponsorGenealogyIdOrSlug,
        shouldNotCreateNewAccount,
        smsOptIn: hasSmsOptIn,
        verticalId: VerticalId.Sendoutcards,
        queryParams: {},
      })
      if (result.__typename === 'VerifyPhoneNumberSuccess') {
        actions.updatedAccount(result.verifiedAccount)
        onSuccess(result, link ?? null)
      } else {
        return result
      }
    } catch (error) {
      throw new Error(error?.toString() ?? 'Failed to verify phone number.')
    }
  }

  const phoneVerificationProps = {
    title: title ?? "Let's get started with your contact information.",
    anchorButton: !!anchorButton
      ? anchorButton
      : isCreateOrLogin
        ? {
            label: 'Already have an account?',
            action: () => {
              actions.clearRouteArgs()
              actions.openAccount()
            },
          }
        : undefined,
    isCreateOrLogin,
    shouldDisplaySmsOptIn: true,
    verifyPhoneNumber: verifyPhoneNumber,
    onChangeFirstAndLastName: onChangeFirstAndLastName,
    shouldHideCardHeroImg: shouldHideCardHeroImg,
    validateButtonTitle: validateButtonTitle,
  }

  return dialog ? (
    <PhoneNumberVerificationDialog
      isOpen={dialog.isOpen}
      onClose={dialog.onClose}
      resourceType={isCreateOrLogin ? 'FREE_CARD' : 'OTHER'}
      {...phoneVerificationProps}
    />
  ) : (
    <PhoneNumberVerificationRaw {...phoneVerificationProps} />
  )
}
